import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Download, LogOut, RefreshCw, Users, Sparkles, Pencil } from 'lucide-react';
import { adminClient, clearToken, TOKEN_KEY, API } from './adminApi';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({ total: 0, by_archetype: [] });
  const [submissions, setSubmissions] = useState([]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [s, sub] = await Promise.all([
        adminClient.get('/admin/stats'),
        adminClient.get('/admin/submissions'),
      ]);
      setStats(s.data);
      setSubmissions(sub.data.submissions || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        navigate('/admin/login', { replace: true });
        return;
      }
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleLogout = () => {
    clearToken();
    navigate('/admin/login', { replace: true });
  };

  const handleExport = async () => {
    // Use a direct request so the browser triggers a real file download.
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const res = await fetch(`${API}/admin/submissions.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `birther-leads-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('CSV export failed.');
    }
  };

  return (
    <section data-testid="admin-dashboard" className="min-h-screen mx-auto w-full max-w-[920px] px-5 sm:px-8 pt-10 pb-16">
      {/* Header */}
      <header className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div className="flex items-center gap-3">
          <div
            className="inline-flex items-center justify-center rounded-full"
            style={{ width: 44, height: 44, backgroundColor: '#3D3830' }}
          >
            <Sparkles size={20} strokeWidth={1.4} color="#FFFFFF" />
          </div>
          <div>
            <p className="section-label" data-testid="admin-eyebrow">Admin Panel</p>
            <h1 className="font-heading text-[26px] sm:text-[32px]" style={{ fontWeight: 500, color: '#3D3830' }}>
              Birther Quiz Leads
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/content" data-testid="admin-content-link" className="btn-ghost" aria-label="Edit content">
            <span className="inline-flex items-center gap-2"><Pencil size={14} strokeWidth={1.6} /> Edit copy</span>
          </Link>
          <button data-testid="admin-refresh" onClick={load} className="btn-ghost" aria-label="Refresh">
            <span className="inline-flex items-center gap-2"><RefreshCw size={14} strokeWidth={1.6} /> Refresh</span>
          </button>
          <button data-testid="admin-logout" onClick={handleLogout} className="btn-ghost" aria-label="Logout">
            <span className="inline-flex items-center gap-2"><LogOut size={14} strokeWidth={1.6} /> Sign out</span>
          </button>
        </div>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="admin-stats">
        <div className="rounded-2xl p-5" style={{ backgroundColor: 'rgba(158,140,168,0.16)', border: '1px solid rgba(158,140,168,0.28)' }}>
          <div className="section-label mb-1">Total leads</div>
          <p className="font-heading text-[36px]" style={{ fontWeight: 500, color: '#3D3830' }} data-testid="admin-total-leads">
            {stats.total}
          </p>
        </div>
        {stats.by_archetype.slice(0, 3).map((a) => (
          <div key={a.archetype} className="rounded-2xl p-5" style={{ backgroundColor: 'rgba(206,197,212,0.22)', border: '1px solid rgba(206,197,212,0.35)' }}>
            <div className="section-label mb-1 truncate">{a.archetype}</div>
            <p className="font-heading text-[28px]" style={{ fontWeight: 500, color: '#3D3830' }}>
              {a.count}
            </p>
          </div>
        ))}
      </div>

      {/* Export */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <p className="font-body text-[14px] inline-flex items-center gap-2" style={{ color: '#6d655a' }}>
          <Users size={14} strokeWidth={1.6} style={{ color: '#3D3830' }} />
          Showing {submissions.length} most recent submissions
        </p>
        <button
          data-testid="admin-export-csv"
          onClick={handleExport}
          className="btn-primary"
          disabled={submissions.length === 0}
        >
          <Download size={14} strokeWidth={1.6} /> Export CSV
        </button>
      </div>

      {/* Errors */}
      {error && (
        <div data-testid="admin-error" className="mb-4 rounded-xl px-4 py-3 font-body text-[13px]"
          style={{ backgroundColor: 'rgba(177,75,75,0.08)', color: '#B14B4B' }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl" style={{ border: '1px solid rgba(206,197,212,0.4)', backgroundColor: 'rgba(255,255,255,0.65)' }}>
        <table className="w-full text-left" data-testid="admin-submissions-table">
          <thead>
            <tr className="font-body text-[11px]" style={{ color: '#3D3830', letterSpacing: '0.16em', textTransform: 'uppercase' }}>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Archetype</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="px-4 py-8 text-center font-body text-[14px]" style={{ color: '#6d655a' }}>Loading…</td></tr>
            )}
            {!loading && submissions.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center font-body text-[14px]" style={{ color: '#6d655a' }}>No submissions yet.</td></tr>
            )}
            {!loading && submissions.map((s) => (
              <tr
                key={s.id}
                className="font-body text-[14px]"
                style={{ borderTop: '1px solid rgba(206,197,212,0.3)', color: '#3D3830' }}
                data-testid={`admin-row-${s.id}`}
              >
                <td className="px-4 py-3 whitespace-nowrap">{(s.created_at || '').slice(0, 10)}</td>
                <td className="px-4 py-3">{s.first_name}</td>
                <td className="px-4 py-3 truncate max-w-[260px]">{s.email}</td>
                <td className="px-4 py-3" style={{ color: '#3D3830', fontWeight: 500 }}>{s.archetype}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
